import {action, makeAutoObservable, observable, runInAction} from "mobx";
import {inject, injectable} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {
	IAuthApiProvider,
	ILoginPayload,
	IUserLoginPayload,
} from "data/providers/api/auth.api.provider";
import type {
	IUserApiProvider,
	IRegistrationPayload,
	IUpdateUserPayload,
} from "data/providers/api/user.api.provider";
import type {
	IForgotPasswordPayload,
	IPasswordApiProvider,
	IResetPasswordPayload,
} from "data/providers/api/password.api.provider";
import type {AxiosResponse} from "axios";
import {uid} from "uid";
import {MLBStorage} from "data/utils/Storage";

export interface IUser {
	id: number;
	email: string;
	username: string;
	uid: string;
	isNotificationsEnabled: boolean;
}

export interface IUserStore {
	get user(): IUser | undefined;
	get isAuthorized(): boolean;
	get wasLoggedOut(): boolean;
	get guestUid(): string;
	get checkGuestUid(): boolean;

	forgotPassword(payload: IForgotPasswordPayload): Promise<AxiosResponse<void>>;
	resetPassword(payload: IResetPasswordPayload): Promise<AxiosResponse<void>>;
	register(payload: IRegistrationPayload): Promise<void>;
	update(payload: IUpdateUserPayload): Promise<void>;
	deactivate(): Promise<void>;
	login(payload: IUserLoginPayload): Promise<void>;
	logout(): Promise<void>;
	backdoor(payload: ILoginPayload): Promise<void>;
	requestUser(): Promise<void>;
	get uid(): string;
	setUid: (uid: string) => void;
	deleteGuestUid: () => void;
}

@injectable()
export class UserStore implements IUserStore {
	static UUID: number | null = null;
	@observable private _user?: IUser = undefined;
	@observable private _wasLoggedOut = false;
	@observable private _uid: string = "";
	@observable private _guestUidKey: string = "guestUid";

	constructor(
		@inject(Bindings.AuthApiProvider) private _authApi: IAuthApiProvider,
		@inject(Bindings.UserApiProvider) private _userApi: IUserApiProvider,
		@inject(Bindings.PasswordApiProvider) private _passwordApi: IPasswordApiProvider
	) {
		makeAutoObservable(this);
	}

	get isAuthorized() {
		return Boolean(this.user);
	}

	get wasLoggedOut() {
		return this._wasLoggedOut;
	}

	get user() {
		return this._user;
	}

	private _generateUID() {
		return uid(10);
	}

	get checkGuestUid() {
		return Boolean(localStorage.getItem(this._guestUidKey));
	}

	get guestUid() {
		let storedUid = localStorage.getItem(this._guestUidKey);

		if (!storedUid) {
			storedUid = this._generateUID();
			localStorage.setItem(this._guestUidKey, storedUid);
		}

		return storedUid;
	}

	deleteGuestUid = () => {
		localStorage.removeItem(this._guestUidKey);
	};

	@action
	async requestUser() {
		const response = await this._userApi.user();
		const {user, xSid} = response.data.success || {};

		runInAction(() => {
			this._uid = user.uid;
			this._user = user;
			UserStore.UUID = user.id;
		});

		if (xSid) {
			MLBStorage.SET("xSid", xSid);
		}
	}

	@action
	async login(payload: IUserLoginPayload) {
		const response = await this._authApi.login(payload);
		const {user, xSid} = response.data.success;

		runInAction(() => {
			this._uid = payload.uid || user.uid;
			this._user = user;
			this._wasLoggedOut = false;
			UserStore.UUID = user.id;
		});

		if (xSid) {
			MLBStorage.SET("xSid", xSid);
		}
	}

	@action
	async register(payload: IRegistrationPayload) {
		const response = await this._userApi.register(payload);
		const {user, xSid} = response.data.success;

		runInAction(() => {
			this._user = user;
			this._wasLoggedOut = false;
			UserStore.UUID = user.id;
		});

		if (xSid) {
			MLBStorage.SET("xSid", xSid);
		}
	}

	@action
	async update(payload: IUpdateUserPayload) {
		const response = await this._userApi.update(payload);
		const {user} = response.data.success;

		runInAction(() => {
			this._user = user;
		});
	}

	@action
	async logout() {
		MLBStorage.REMOVE("xSid");
		runInAction(() => {
			this._user = undefined;
			this._wasLoggedOut = true;
			UserStore.UUID = null;
		});

		await this._authApi.logout();
	}

	@action
	async deactivate() {
		await this._userApi.deactivateAccount();

		runInAction(() => {
			this._user = undefined;
			this._wasLoggedOut = true;
			UserStore.UUID = null;
		});
	}

	@action
	async backdoor(payload: ILoginPayload) {
		const response = await this._authApi.backdoor(payload);
		const {user, xSid} = response.data.success;

		runInAction(() => {
			this._uid = user.uid;
			this._user = user;
			this._wasLoggedOut = false;
			UserStore.UUID = user.id;
		});

		if (xSid) {
			MLBStorage.SET("xSid", xSid);
		}
	}

	forgotPassword(payload: IForgotPasswordPayload) {
		return this._passwordApi.forgotPassword(payload);
	}

	resetPassword(payload: IResetPasswordPayload) {
		return this._passwordApi.resetPassword(payload);
	}

	get uid() {
		return this._uid;
	}

	setUid = (uid: string) => {
		this._uid = uid;
	};
}
